import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Wrench,
  ClipboardList,
  Zap,
  CheckCircle2,
  Users,
  BarChart3,
  DollarSign,
  ArrowRight,
} from 'lucide-react';
import { Button } from './ui/button';

const TENANT_CAPABILITIES = [
  {
    icon: Wrench,
    color: 'bg-orange-100',
    iconColor: 'text-orange-600',
    title: 'Report Issues Instantly',
    description:
      'Describe any problem in plain English: leaks, outages, broken fixtures. Niva captures every detail and files the request automatically.',
  },
  {
    icon: ClipboardList,
    color: 'bg-blue-100',
    iconColor: 'text-blue-600',
    title: 'Track Your Requests',
    description:
      'Ask "What\'s the status of my repair?" anytime. Niva knows every case number, priority, and who\'s assigned.',
  },
  {
    icon: Zap,
    color: 'bg-red-100',
    iconColor: 'text-red-600',
    title: '24/7 Emergency Support',
    description:
      'Gas leak at midnight? Niva escalates emergencies immediately. No waiting for office hours.',
  },
  {
    icon: CheckCircle2,
    color: 'bg-green-100',
    iconColor: 'text-green-600',
    title: 'Close the Loop',
    description:
      'Once your issue is fixed, just tell Niva. It marks the case resolved and keeps your history clean.',
  },
];

const LANDLORD_CAPABILITIES = [
  {
    icon: Users,
    color: 'bg-blue-100',
    iconColor: 'text-blue-600',
    title: 'Instant Tenant Lookups',
    description:
      'Ask about any tenant by name or property to get their lease dates, contact info, unit, and status in seconds.',
  },
  {
    icon: Wrench,
    color: 'bg-orange-100',
    iconColor: 'text-orange-600',
    title: 'Maintenance at a Glance',
    description:
      'Ask for open requests, pending approvals, or recent resolutions across all your properties. No dashboard diving.',
  },
  {
    icon: DollarSign,
    color: 'bg-green-100',
    iconColor: 'text-green-600',
    title: 'Financial Clarity',
    description:
      'Get maintenance cost summaries, outstanding approvals, and rent collection status with a single question.',
  },
  {
    icon: BarChart3,
    color: 'bg-violet-100',
    iconColor: 'text-violet-600',
    title: 'Portfolio Oversight',
    description:
      'Niva knows your entire portfolio. Ask about performance, vacancies, vendor assignments, or upcoming inspections.',
  },
];

const WelcomeOnboarding = ({ user }) => {
  const navigate = useNavigate();
  const isTenant = user?.userType === 'Tenant';
  const firstName = (user?.name || user?.username || '').split(' ')[0] || 'there';
  const capabilities = isTenant ? TENANT_CAPABILITIES : LANDLORD_CAPABILITIES;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex flex-col">
      {/* Top bar */}
      <div className="border-b bg-white/80 backdrop-blur-sm">
        <div className="container mx-auto px-6 py-4 flex items-center gap-2">
          <img src="/nivasapro.ai-app-Icon.png" alt="NivasaPro" className="h-6 w-6" />
          <span className="text-lg font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
            NivasaPro.ai
          </span>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-12">
        <div className="w-full max-w-2xl space-y-10">

          {/* Niva avatar + heading */}
          <div className="flex flex-col items-center text-center space-y-4">
            <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/25">
              <Sparkles className="h-8 w-8 text-white" />
            </div>
            <div className="space-y-2">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
                You're in, {firstName}!
              </h1>
              <p className="text-lg text-muted-foreground max-w-md mx-auto">
                Meet <span className="font-semibold text-cyan-600">Niva</span>, your AI assistant,
                available around the clock.{' '}
                {isTenant
                  ? "Here's what you can ask me to do."
                  : "Here's what I can do for you."}
              </p>
            </div>
          </div>

          {/* Capability cards */}
          <div className="grid sm:grid-cols-2 gap-4">
            {capabilities.map((cap, i) => {
              const Icon = cap.icon;
              return (
                <div
                  key={i}
                  className="flex gap-4 p-5 bg-white rounded-xl border border-gray-200 hover:border-cyan-200 hover:shadow-md transition-all"
                >
                  <div className={`h-10 w-10 rounded-lg ${cap.color} flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`h-5 w-5 ${cap.iconColor}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{cap.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{cap.description}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* CTA */}
          <div className="flex flex-col items-center gap-3">
            <Button
              onClick={() => navigate('/niva')}
              size="lg"
              className="gap-2 px-8 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white rounded-xl shadow-md shadow-cyan-500/20 text-base"
            >
              Let's go, I'm ready
              <ArrowRight className="h-4 w-4" />
            </Button>
            <p className="text-xs text-muted-foreground">You can always come back to this from your account settings</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeOnboarding;
